import { Link, useLocation } from "react-router-dom";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  VideoIcon,
  PhoneIcon,
  MailIcon,
  LinkedinIcon,
  FacebookIcon,
  InstagramIcon,
  PinterestIcon,
} from "../icons/NavbarIcons";
import { scrollToSection } from "../../utils/scroll";
import { LANDING_SECTIONS, CONTACT_INFO, SOCIAL_LINKS } from "./constants";
import { ARTICLES_VISIBLE } from "../../config/features";

type MobileMenuProps = {
  isMenuOpen: boolean;
  isDropdownOpen: boolean;
  toggleDropdown: () => void;
  handleNavigation: (sectionId: string) => void;
  setIsMenuOpen: (open: boolean) => void;
};

export function MobileMenu({
  isMenuOpen,
  isDropdownOpen,
  toggleDropdown,
  handleNavigation,
  setIsMenuOpen,
}: MobileMenuProps) {
  const location = useLocation();

  const isServicesPage = location.pathname.endsWith("/services");
  const isResourcesPage = location.pathname.endsWith("/resources");
  const isHomePage = location.pathname === "/";
  const isCareersPage = location.pathname.endsWith("/careers");
  const isBookingPage = location.pathname.endsWith("/booking");
  const isArticlesPage = ARTICLES_VISIBLE && location.pathname.startsWith("/articles");
  const isCartoonsPage = location.pathname.startsWith("/cartoons");

  const showHomeLink =
    isServicesPage ||
    isResourcesPage ||
    isCareersPage ||
    isBookingPage ||
    isArticlesPage ||
    isCartoonsPage;

  const renderSocialIcon = (iconName: string) => {
    const className = "w-5 h-5";
    switch (iconName) {
      case "LinkedinIcon":
        return <LinkedinIcon className={className} />;
      case "FacebookIcon":
        return <FacebookIcon className={className} />;
      case "InstagramIcon":
        return <InstagramIcon className={className} />;
      case "PinterestIcon":
        return <PinterestIcon className={className} />;
      default:
        return null;
    }
  };

  return (
    <div
      className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
        isMenuOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
      }`}
    >
      <div className="bg-white shadow-lg rounded-b-lg px-4 py-3">
        <nav className="flex flex-col space-y-4 pb-3">
          {/* Home Link */}
          {showHomeLink && (
            <Link
              to="/"
              className="block py-2 px-4 text-gray-800 font-semibold hover:bg-blue-50 rounded-lg transition-colors"
            >
              Home
            </Link>
          )}

          {/* Sections dropdown for Home Page */}
          {isHomePage && (
            <div className="py-2 px-4">
              <div
                className="flex justify-between items-center text-gray-800 font-semibold cursor-pointer"
                onClick={toggleDropdown}
              >
                <span>Sections</span>
                {isDropdownOpen ? (
                  <ChevronUpIcon className="h-5 w-5" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5" />
                )}
              </div>

              {/* Mobile dropdown content */}
              <div
                className={`pl-4 pb-2 space-y-1 bg-gray-50 border-t border-gray-100 mt-2 ${
                  isDropdownOpen ? "block" : "hidden"
                }`}
              >
                {LANDING_SECTIONS.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => handleNavigation(section.id)}
                    className="block py-2 w-full text-left text-gray-700 hover:text-blue-500"
                  >
                    {section.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <Link
            to="/booking"
            className="block py-2 px-4 text-gray-800 font-semibold hover:bg-blue-50 rounded-lg transition-colors"
          >
            Book Now
          </Link>

          <Link
            to="/services"
            className="block py-2 px-4 text-gray-800 font-semibold hover:bg-blue-50 rounded-lg transition-colors"
          >
            Pay Here
          </Link>

          <Link
            to="/resources"
            className="block py-2 px-4 font-semibold hover:bg-blue-50 rounded-lg transition-colors animate-blink-orange"
          >
            Resources
          </Link>

          {ARTICLES_VISIBLE ? (
            <Link
              to="/articles"
              className="block py-2 px-4 text-gray-800 font-semibold hover:bg-blue-50 rounded-lg transition-colors"
            >
              Articles
            </Link>
          ) : null}

          <Link
            to="/cartoons"
            className="block py-2 px-4 text-gray-800 font-semibold hover:bg-blue-50 rounded-lg transition-colors"
          >
            Comics
          </Link>

          <Link
            to="/careers"
            className="block py-2 px-4 text-gray-800 font-semibold hover:bg-blue-50 rounded-lg transition-colors"
          >
            Careers
          </Link>

          <button
            onClick={() => {
              scrollToSection("appointment");
              setIsMenuOpen(false);
            }}
            className="btn btn-secondary btn-md w-full"
          >
            Contact Us
          </button>

          {/* Psychology Today logo */}
          <div className="flex justify-center py-2">
            <img
              src="/assets/home/psychology-today-logo-160.webp"
              width={160}
              height={52}
              className="w-32"
              alt="Psychology Today"
            />
          </div>
        </nav>

        {/* Mobile social links */}
        <div className="pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500 mb-3">Connect with us:</p>
          <div className="flex items-center space-x-4">
            {SOCIAL_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={link.label}
                className="text-gray-600 hover:text-blue-500"
              >
                {renderSocialIcon(link.iconName)}
              </a>
            ))}
          </div>

          {/* Mobile contact info */}
          <div className="mt-4 space-y-2">
            <a
              href={CONTACT_INFO.zoom}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-gray-600 hover:text-blue-500"
            >
              <VideoIcon className="w-4 h-4 mr-2" />
              <span className="text-sm">Zoom Meeting</span>
            </a>
            <a
              href={`tel:${CONTACT_INFO.phoneRaw}`}
              className="flex items-center text-gray-600 hover:text-blue-500"
            >
              <PhoneIcon className="w-4 h-4 mr-2" />
              <span className="text-sm">{CONTACT_INFO.phone}</span>
            </a>

            <a
              href={`mailto:${CONTACT_INFO.email}`}
              className="flex items-center text-gray-600 hover:text-blue-500"
            >
              <MailIcon className="w-4 h-4 mr-2" />
              <span className="text-sm">{CONTACT_INFO.email}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
